import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import type { PmfChartHandle } from '../render/pmf-chart';
import { createPmfChart } from '../render/pmf-chart';
import { createSvgEl } from '@learnforge/demo-render';
import type { ProbContext, ProbKey } from '../types';
import { createSliderRow, createToggleRow } from '@learnforge/demo-kit';
import { paramFormat } from '../ui/param-format';
import { createActionBar } from '../action-bar';

// ============================================================================
// Exported pure helpers (keep unchanged — tested + used by registry)
// ============================================================================

export type BaseDistId = 'uniform' | 'exponential' | 'bimodal' | 'discrete-die' | 'custom-3-point';

export interface BaseDistSpec {
  id: BaseDistId;
  mean: number;
  variance: number;
}

export function baseDistSpec(id: BaseDistId): BaseDistSpec {
  switch (id) {
    case 'uniform':
      return { id, mean: 0, variance: 1 / 3 };
    case 'exponential':
      return { id, mean: 1, variance: 1 };
    case 'bimodal':
      return { id, mean: 0, variance: 1 };
    case 'discrete-die':
      return { id, mean: 3.5, variance: 35 / 12 };
    case 'custom-3-point':
      return { id, mean: 0, variance: 2 / 3 };
  }
}

export function sampleBase(id: BaseDistId, prng: import('../prob/prng').Prng): number {
  const u = prng.next();
  switch (id) {
    case 'uniform':
      return 2 * u - 1;
    case 'exponential':
      return -Math.log(1 - u);
    case 'bimodal': {
      // 50/50 mixture of N(-2, 0.5) and N(2, 0.5) using Box-Muller
      const r = Math.sqrt(-2 * Math.log(1 - u));
      const theta = 2 * Math.PI * prng.next(); // 2nd prng.next() call for bimodal
      const z = r * Math.cos(theta);
      return u < 0.5 ? -2 + 0.5 * z : 2 + 0.5 * z;
    }
    case 'discrete-die':
      return Math.floor(u * 6) + 1;
    case 'custom-3-point':
      return [-1, 0, 1][prng.int(3)]!; // prng.int(3) for custom-3-point
  }
}

export function sumOfN(id: BaseDistId, n: number, prng: import('../prob/prng').Prng): number {
  let s = 0;
  for (let i = 0; i < n; i++) {
    s += sampleBase(id, prng);
  }
  return s;
}

export function meanOfN(id: BaseDistId, n: number, prng: import('../prob/prng').Prng): number {
  return sumOfN(id, n, prng) / n;
}

export function theoreticalMeanVariance(
  spec: BaseDistSpec,
  n: number,
  mode: 'sum' | 'mean',
): { mean: number; variance: number; sigma: number } {
  if (mode === 'sum') {
    const variance = n * spec.variance;
    return { mean: n * spec.mean, variance, sigma: Math.sqrt(variance) };
  }
  const variance = spec.variance / n;
  return { mean: spec.mean, variance, sigma: Math.sqrt(variance) };
}

// ============================================================================
// State (model only — no transient DOM refs)
// ============================================================================

interface State {
  baseId: BaseDistId;
  n: number;
  mode: 'sum' | 'mean';
  standardize: boolean;
  showNormal: boolean;
  showSomregel: boolean;
  histogram: Map<number, number>;
  sampleCount: number;
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): CustomExperimentSpec<State, ProbKey, ProbContext> {
  return {
    labelKey: 'experiment.sums-of-iid.label',
    demoId: 'probability',
    advancedKey: 'sums-of-iid',

    initialState(): State {
      return {
        baseId: 'uniform',
        n: 10,
        mode: 'sum',
        standardize: false,
        showNormal: true,
        showSomregel: true,
        histogram: new Map<number, number>(),
        sampleCount: 0,
      };
    },

    createRender(ctx, host, controls) {
      // ── Stage SVG (built once) ──
      const stageSvg = createSvgEl('svg', { viewBox: '0 0 800 460', class: 'pb-soi-svg' });
      host.appendChild(stageSvg);
      const stageGroup = createSvgEl('g');
      stageSvg.appendChild(stageGroup);

      // pmf chart handle — destroyed and recreated on each update
      let chart: PmfChartHandle | null = null;

      // ── Panel (built once) ──

      // Base distribution select
      const baseLbl = document.createElement('label');
      baseLbl.textContent = ctx.i18n.t('param.base-dist.label');
      const baseSel = document.createElement('select');
      for (const id of [
        'uniform',
        'exponential',
        'bimodal',
        'discrete-die',
        'custom-3-point',
      ] as BaseDistId[]) {
        const o = document.createElement('option');
        o.value = id;
        o.textContent = ctx.i18n.t(`submode.sums-of-iid.${id}.label` as never);
        baseSel.appendChild(o);
      }
      baseSel.addEventListener('change', () => {
        controls.setState((s) => ({
          ...s,
          baseId: baseSel.value as BaseDistId,
          histogram: new Map<number, number>(),
          sampleCount: 0,
        }));
      });
      const baseRow = document.createElement('div');
      baseRow.className = 'pb-dropdown-row';
      baseRow.append(baseLbl, baseSel);
      ctx.panel.appendChild(baseRow);

      // n slider — we keep a reference so update() can reconcile the displayed value
      const nSliderHandle = createSliderRow({
        id: 'soi-n',
        label: ctx.i18n.t('param.n.label'),
        i18n: ctx.i18n,
        min: 1,
        max: 50,
        step: 1,
        value: 10,
        format: paramFormat,
        onChange: (v) => {
          controls.setState((s) => ({
            ...s,
            n: v,
            histogram: new Map<number, number>(),
            sampleCount: 0,
          }));
        },
      });
      ctx.panel.appendChild(nSliderHandle.element);

      // Sum / mean select
      const modeLbl = document.createElement('label');
      modeLbl.textContent = ctx.i18n.t('param.sum-or-mean.label');
      const modeSel = document.createElement('select');
      for (const m of ['sum', 'mean'] as const) {
        const o = document.createElement('option');
        o.value = m;
        o.textContent = m;
        modeSel.appendChild(o);
      }
      modeSel.addEventListener('change', () => {
        controls.setState((s) => ({
          ...s,
          mode: modeSel.value as 'sum' | 'mean',
          histogram: new Map<number, number>(),
          sampleCount: 0,
        }));
      });
      const modeRow = document.createElement('div');
      modeRow.className = 'pb-dropdown-row';
      modeRow.append(modeLbl, modeSel);
      ctx.panel.appendChild(modeRow);

      // Checkboxes — initial checked values come FROM initial state (not hardcoded).
      // initialState: standardize=false, showNormal=true, showSomregel=true.

      const standardizeToggle = createToggleRow({
        labelKey: 'param.standardize.label',
        i18n: ctx.i18n,
        checked: false,
        onChange: (checked) => {
          controls.setState((s) => ({ ...s, standardize: checked }));
        },
      });
      ctx.panel.appendChild(standardizeToggle.element);

      const normalToggle = createToggleRow({
        labelKey: 'param.show-normal-approx.label',
        i18n: ctx.i18n,
        checked: true,
        onChange: (checked) => {
          controls.setState((s) => ({ ...s, showNormal: checked }));
        },
      });
      ctx.panel.appendChild(normalToggle.element);

      const somregelToggle = createToggleRow({
        labelKey: 'param.somregel.label',
        i18n: ctx.i18n,
        checked: true,
        onChange: (checked) => {
          controls.setState((s) => ({ ...s, showSomregel: checked }));
        },
      });
      ctx.panel.appendChild(somregelToggle.element);

      // ── Draw helper: accumulate `times` samples into state immutably ──
      // PRNG ORDER (CRITICAL): sampleBase/sumOfN/meanOfN consume ctx.prng in the
      // exact original order — bimodal uses 2 calls (u + theta), custom-3-point
      // uses prng.int(3). This is preserved because we call the same helpers.
      function makeDraw(times: number) {
        return () => {
          controls.setState((s) => {
            const h = new Map(s.histogram);
            let count = s.sampleCount;
            for (let i = 0; i < times; i++) {
              const spec = baseDistSpec(s.baseId);
              let value =
                s.mode === 'mean'
                  ? meanOfN(s.baseId, s.n, ctx.prng)
                  : sumOfN(s.baseId, s.n, ctx.prng);
              if (s.standardize) {
                const tm = theoreticalMeanVariance(spec, s.n, s.mode);
                value = (value - tm.mean) / tm.sigma;
              }
              const bin = Math.round(value * 10) / 10;
              h.set(bin, (h.get(bin) ?? 0) + 1);
              count++;
            }
            return { ...s, histogram: h, sampleCount: count };
          });
        };
      }

      // ── Action bar (built once) ──
      const actionBar = createActionBar(ctx.actionBar, [
        {
          id: 'btn.draw-100',
          labelKey: ctx.i18n.t('btn.draw-100' as never),
          onClick: makeDraw(100),
        },
        {
          id: 'btn.draw-1000',
          labelKey: ctx.i18n.t('btn.draw-1000' as never),
          onClick: makeDraw(1000),
        },
        {
          id: 'btn.draw-10000',
          labelKey: ctx.i18n.t('btn.draw-10000' as never),
          onClick: makeDraw(10000),
        },
        {
          id: 'btn.reset',
          labelKey: ctx.i18n.t('btn.reset' as never),
          onClick: () => {
            controls.setState((s) => ({
              ...s,
              histogram: new Map<number, number>(),
              sampleCount: 0,
            }));
          },
        },
      ]);

      // ── RenderHandle ──
      const update = (state: State): void => {
        // Destroy previous chart, recreate from state
        chart?.destroy();
        stageGroup.replaceChildren();

        const bars = [...state.histogram.entries()]
          .map(([x, c]) => ({ x, p: c / Math.max(state.sampleCount, 1) }))
          .sort((a, b) => a.x - b.x);
        const xMin = bars.length > 0 ? Math.min(...bars.map((b) => b.x)) : -3;
        const xMax = bars.length > 0 ? Math.max(...bars.map((b) => b.x)) : 3;
        const spec = baseDistSpec(state.baseId);
        const tm = theoreticalMeanVariance(spec, state.n, state.mode);

        const opts: Parameters<typeof createPmfChart>[1] = {
          xDomain: [xMin - 0.5, xMax + 0.5],
          bars,
          xLabelKey: 'axis.value',
          yLabelKey: 'axis.probability',
          i18n: ctx.i18n,
        };
        if (state.showNormal) {
          opts.normalOverlay = {
            mu: state.standardize ? 0 : tm.mean,
            sigma: state.standardize ? 1 : tm.sigma,
            opacity: 0.6,
          };
        }
        chart = createPmfChart(stageGroup, opts);

        if (state.showSomregel) {
          const txt = createSvgEl('text', {
            class: 'pb-soi-somregel',
            x: 20,
            y: 440,
          });
          txt.textContent = `${ctx.i18n.t('formula.somregel.mu')}: μ_S = n·μ = ${tm.mean.toFixed(3)}    ${ctx.i18n.t('formula.somregel.sigma')}: σ_S = σ·√n = ${tm.sigma.toFixed(3)}`;
          stageGroup.appendChild(txt);
        }

        // Reflect selects and checkboxes from state
        baseSel.value = state.baseId;
        modeSel.value = state.mode;
        nSliderHandle.setValue(state.n);
        standardizeToggle.setChecked(state.standardize);
        normalToggle.setChecked(state.showNormal);
        somregelToggle.setChecked(state.showSomregel);
      };

      return {
        update,
        dispose() {
          chart?.destroy();
          actionBar.destroy();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// Registry alias — keeps index.ts and the existing registry working unchanged.
export const mountSumsOfIid = experiment.mount.bind(experiment);
